import React, { useState, useEffect, useContext } from 'react';
import { getProviderRequest, approveProviderRequest, rejectProviderRequest } from '../../../hooks/api/odoo';
import { keyUserActiveEnterpriseId } from '../../../assets/js/SessionStorageNames';
import { GlobalContext } from '../../../contexts/GlobalContext';
import ProductCardWidget from './ProductCardWidget';
import ProductModalWidget from './ProductModalWidget';
import { AdditionalImagesSliderModal } from '../AdditionalImagesSliderModal';
import TabsWidget from './TabsWidget';
import PaginationWidget from './PaginationWidget';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faHourglassHalf, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';

// Función para marcar un producto como revisado
const markProductAsReviewed = async (productId) => {
  try {
    // Obtener productos revisados del localStorage
    const reviewedProducts = JSON.parse(localStorage.getItem('reviewedProducts') || '[]');
    
    // Agregar el producto a la lista si no existe
    if (!reviewedProducts.includes(productId)) {
      reviewedProducts.push(productId);
      localStorage.setItem('reviewedProducts', JSON.stringify(reviewedProducts));
    }
    
    console.log('Producto marcado como revisado:', productId);
    console.log('Productos revisados actuales:', reviewedProducts);
    
    return { success: true, message: 'Producto marcado como revisado' };
  } catch (error) {
    console.error('Error al marcar como revisado:', error);
    return { success: false, message: 'Error al marcar como revisado' };
  }
};

const TABS = [
  { key: 'PENDING', label: 'Por aprobar', icon: faHourglassHalf },
  { key: 'PROCESSING', label: 'En proceso', icon: faClipboardCheck },
  { key: 'APPROVED', label: 'Aprobados', icon: faCheckCircle },
  { key: 'REJECTED', label: 'Rechazados', icon: faTimesCircle },
  { key: 'FAILED', label: 'Fallidos', icon: faTimesCircle },
];

// Diccionario de traducción de estados
const STATUS_LABELS = {
  pending: 'Pendiente',
  processing: 'En proceso',
  approved: 'Aprobado',
  rejected: 'Rechazado',
};

const ProductReviewClient = () => {
  const globalContext = useContext(GlobalContext);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(''); // 'approve' | 'reject'
  const [rejectReason, setRejectReason] = useState('');
  const [approveComment, setApproveComment] = useState('');
  const [selectedTab, setSelectedTab] = useState('PENDING');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  // Estados para paginación
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 5, hasNextPage: false, hasPrevPage: false });
  // Estado para slider de imágenes adicionales
  const [sliderOpen, setSliderOpen] = useState(false);
  const [sliderImages, setSliderImages] = useState([]);
  // Estados para selección múltiple
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Función para verificar si un producto está revisado
  const isProductReviewed = (productId) => {
    try {
      const reviewedProducts = JSON.parse(localStorage.getItem('reviewedProducts') || '[]');
      return reviewedProducts.includes(productId);
    } catch (error) {
      console.error('Error al verificar si está revisado:', error);
      return false;
    }
  };

  // Obtener productos seleccionables (revisados en estados PENDING/FAILED)
  const getSelectableProducts = () => {
    return products.filter(product => {
      const statusUpper = String(product.status).toUpperCase();
      const isReviewed = isProductReviewed(product.id);
      return (statusUpper === 'PENDING' || statusUpper === 'FAILED') && isReviewed;
    });
  };

  // Función para seleccionar/deseleccionar todos
  const handleSelectAll = () => {
    const selectableProducts = getSelectableProducts();
    const allSelected = selectedProducts.length === selectableProducts.length;
    
    if (allSelected) {
      // Deseleccionar todos
      setSelectedProducts([]);
    } else {
      // Seleccionar todos
      setSelectedProducts(selectableProducts);
    }
  };

  // Función para convertir base64 puro a data URL para mostrar imágenes
  const convertBase64ToDataUrl = (base64String) => {
    if (!base64String) return null;
    // Si ya tiene el prefijo data:, retornarlo directamente
    if (base64String.startsWith('data:')) {
      return base64String;
    }
    // Si es base64 puro, agregar el prefijo
    return `data:image/png;base64,${base64String}`;
  };

  // Función para convertir múltiples imágenes
  const convertImagesToDataUrl = (images) => {
    if (!images || images.length === 0) return [];
    return images.map(img => convertBase64ToDataUrl(img));
  };

  const fetchProducts = async (status, pageNumber = page, pageItemCount = itemsPerPage) => {
    setLoading(true);
    setError(null);
    try {
      // Obtener el enterpriseId dinámicamente
      const entId = localStorage.getItem(keyUserActiveEnterpriseId);
      const data = await getProviderRequest(entId, pageNumber, pageItemCount, status);
      console.log('Respuesta de getProviderRequest:', data);
      setProducts(Array.isArray(data?.requests) ? data.requests : []);
      setPagination(data?.pagination || { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: pageItemCount, hasNextPage: false, hasPrevPage: false });
    } catch (err) {
      setError('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(selectedTab, page, itemsPerPage);
    // eslint-disable-next-line
  }, [selectedTab, page, itemsPerPage]);

  // Nuevo: recargar productos cuando cambie la empresa
  useEffect(() => {
    fetchProducts(selectedTab, 1, itemsPerPage); // Reinicia a la página 1
    setPage(1);
    // eslint-disable-next-line
  }, [globalContext.currentEnterpriseId]);

  const handleApprove = (product) => {
    setSelectedProduct(product);
    setPopupType('approve');
    setShowPopup(true);
  };

  const handleReject = (product) => {
    setSelectedProduct(product);
    setPopupType('reject');
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setRejectReason('');
    setApproveComment('');
  };

  const handleConfirm = async () => {
    console.log('handleConfirm ejecutado', { popupType, selectedProduct });
    if (popupType === 'reject' && !rejectReason.trim()) return;
    if (popupType === 'approve') {
      // Lógica para aprobar
      try {
        const entId = localStorage.getItem(keyUserActiveEnterpriseId);
        const productId = selectedProduct?.id;
        console.log('Intentando aprobar', { entId, productId });
        if (!entId || !productId) {
          setError('Faltan datos para aprobar el producto');
          return;
        }
        const resp = await approveProviderRequest(entId, productId);
        console.log('Respuesta approveProviderRequest', resp);
        if (resp) {
          console.log('Antes de eliminar:', products);
          const nuevosProductos = products.filter(p => p.id !== selectedProduct.id);
          console.log('Después de eliminar:', nuevosProductos);
          setProducts(nuevosProductos);
          setSuccessMessage('¡Has aprobado correctamente un producto!');
          setSelectedProduct(null); // Limpia el producto seleccionado
          setShowPopup(false);      // Cierra el popup
        } else {
          setError('No se pudo aprobar el producto.');
        }
      } catch (e) {
        setError('Error al aprobar el producto');
        console.error('Error en approveProviderRequest', e);
      }
    } else if (popupType === 'reject') {
      // Lógica para rechazar
      try {
        const entId = localStorage.getItem(keyUserActiveEnterpriseId);
        const productId = selectedProduct?.id;
        console.log('Intentando rechazar', { entId, productId, rejectReason });
        if (!entId || !productId) {
          setError('Faltan datos para rechazar el producto');
          return;
        }
        const resp = await rejectProviderRequest(entId, productId, rejectReason);
        console.log('Respuesta rejectProviderRequest', resp);
        if (resp) {
          setProducts(products.filter(p => p.id !== selectedProduct.id));
          setSuccessMessage('¡Has rechazado correctamente un producto!');
        } else {
          setError('No se pudo rechazar el producto.');
        }
      } catch (e) {
        setError('Error al rechazar el producto');
        console.error('Error en rejectProviderRequest', e);
      }
    }
    setShowPopup(false);
    setSelectedProduct(null);
    setRejectReason('');
    setApproveComment('');
    // Ocultar mensaje después de 3 segundos
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Función para manejar el marcado como revisado
  const handleMarkAsReviewed = async (product) => {
    try {
      const productId = product?.id;
      console.log('Intentando marcar como revisado', { productId });
      
      if (!productId) {
        setError('Faltan datos para marcar como revisado');
        return;
      }
      
      const resp = await markProductAsReviewed(productId);
      console.log('Respuesta markProductAsReviewed', resp);
      
      if (resp && resp.success) {
        setSuccessMessage('¡Producto marcado como revisado!');
        setTimeout(() => setSuccessMessage(''), 3000);
        
        // Forzar re-render de las tarjetas para actualizar el estado visual
        setProducts([...products]);
        
        // Disparar evento para actualización en tiempo real
        window.dispatchEvent(new CustomEvent('productReviewed', {
          detail: { productId: productId }
        }));
      } else {
        setError('No se pudo marcar como revisado.');
      }
    } catch (e) {
      setError('Error al marcar como revisado');
      console.error('Error en markProductAsReviewed', e);
    }
  };

  // Funciones para selección múltiple
  const handleProductSelect = (product) => {
    setSelectedProducts(prev => [...prev, product]);
  };

  const handleProductDeselect = (product) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== product.id));
  };

  const handleBulkApprove = async () => {
    if (selectedProducts.length === 0) return;
    
    console.log('Enviando', selectedProducts.length, 'productos para aprobar:', selectedProducts.map(p => p.id));
    
    setBulkActionLoading(true);
    try {
      const entId = localStorage.getItem(keyUserActiveEnterpriseId);
      
      // // Enviar cada producto al endpoint
      // for (const product of selectedProducts) {
      //   console.log('Aprobando producto:', product.id);
      // }
      await approveProviderRequest(entId, selectedProducts.map(p => p.id));
      
      setSuccessMessage(`¡${selectedProducts.length} productos aprobados exitosamente!`);
      
      // Remover todos los productos seleccionados de la lista
      setProducts(prev => prev.filter(p => !selectedProducts.some(sp => sp.id === p.id)));
      
      setSelectedProducts([]);
    } catch (error) {
      setError('Error al aprobar productos en lote');
      console.error('Error en bulk approve:', error);
    } finally {
      setBulkActionLoading(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleBulkReject = async () => {
    if (selectedProducts.length === 0) return;
    
    console.log('Enviando', selectedProducts.length, 'productos para rechazar:', selectedProducts.map(p => p.id));
    
    setBulkActionLoading(true);
    try {
      const entId = localStorage.getItem(keyUserActiveEnterpriseId);
      const rejectReason = 'Rechazado en lote por administrador';
      
      // Enviar cada producto al endpoint
      for (const product of selectedProducts) {
        console.log('Rechazando producto:', product.id);
        await rejectProviderRequest(entId, product.id, rejectReason);
      }
      
      setSuccessMessage(`¡${selectedProducts.length} productos rechazados exitosamente!`);
      
      // Remover todos los productos seleccionados de la lista
      setProducts(prev => prev.filter(p => !selectedProducts.some(sp => sp.id === p.id)));
      
      setSelectedProducts([]);
    } catch (error) {
      setError('Error al rechazar productos en lote');
      console.error('Error en bulk reject:', error);
    } finally {
      setBulkActionLoading(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  return (
    <div style={{ background: '#f7f7f9', minHeight: '100vh', padding: 32 }}>
      {/* Mensaje de éxito tipo toast */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: 30,
          right: 30,
          background: successMessage.includes('rechazado') ? '#e74c3c' : '#27ae60',
          color: '#fff',
          padding: '16px 32px',
          borderRadius: 10,
          boxShadow: '0 2px 12px #0002',
          fontWeight: 600,
          fontSize: 18,
          zIndex: 3000,
          transition: 'opacity 0.3s',
        }}>
          {successMessage}
        </div>
      )}
      <div style={{ maxWidth: 1300, margin: '0 auto' }}>
        <h2 style={{ fontWeight: 700, fontSize: 28, marginBottom: 24 }}>Revisión de productos</h2>
        {/* Tabs de estado */}
        <TabsWidget
          tabs={TABS}
          selectedTab={selectedTab}
          onTabSelect={(key) => { 
            setSelectedTab(key); 
            setPage(1); 
            setSelectedProducts([]); // Limpiar selección al cambiar de tab
          }}
        />
        {/* Paginación y select de items por página (ahora debajo de los tabs) */}
        <PaginationWidget
          page={page}
          totalPages={pagination.totalPages}
          onPageChange={setPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={(num) => { setItemsPerPage(num); setPage(1); }}
          totalItems={pagination.totalItems}
          onRefresh={() => { setPage(1); fetchProducts(selectedTab, 1, itemsPerPage); }}
          showSelectAll={selectedTab === 'PENDING' || selectedTab === 'FAILED'}
          onSelectAll={handleSelectAll}
          selectedCount={selectedProducts.length}
          totalSelectable={getSelectableProducts().length}
        />
        {loading ? (
          <div style={{ textAlign: 'center', color: '#888', fontSize: 18 }}>Cargando productos...</div>
        ) : error ? (
          <div style={{ textAlign: 'center', color: '#e74c3c', fontSize: 18 }}>{error}</div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#888', fontSize: 18 }}>No hay productos en esta categoría.</div>
        ) : (
          <>
            {/* Botones de acción múltiple - Solo en estados PENDING/FAILED */}
            {selectedProducts.length > 0 && (selectedTab === 'PENDING' || selectedTab === 'FAILED') && (
              <div style={{
                background: '#fff',
                borderRadius: 12,
                padding: '16px 24px',
                marginBottom: 20,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, color: '#333' }}>
                    {selectedProducts.length} producto{selectedProducts.length !== 1 ? 's' : ''} seleccionado{selectedProducts.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    onClick={handleBulkApprove}
                    disabled={bulkActionLoading}
                    style={{
                      background: '#27ae60',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 20px',
                      fontWeight: 600,
                      cursor: bulkActionLoading ? 'not-allowed' : 'pointer',
                      opacity: bulkActionLoading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    {bulkActionLoading ? (
                      <>
                        <div style={{
                          width: 16,
                          height: 16,
                          border: '2px solid #ffffff40',
                          borderTop: '2px solid #fff',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        Aprobando...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} />
                        Aprobar seleccionados
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleBulkReject}
                    disabled={bulkActionLoading}
                    style={{
                      background: '#e74c3c',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 20px',
                      fontWeight: 600,
                      cursor: bulkActionLoading ? 'not-allowed' : 'pointer',
                      opacity: bulkActionLoading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    {bulkActionLoading ? (
                      <>
                        <div style={{
                          width: 16,
                          height: 16,
                          border: '2px solid #ffffff40',
                          borderTop: '2px solid #fff',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        Rechazando...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faTimesCircle} />
                        Rechazar seleccionados
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {products.map(product => (
                <ProductCardWidget
                  key={product.id}
                  product={product}
                  selectedTab={selectedTab}
                  selectedProduct={selectedProduct}
                  setSelectedProduct={setSelectedProduct}
                  convertBase64ToDataUrl={convertBase64ToDataUrl}
                  selectedProducts={selectedProducts}
                  onProductSelect={handleProductSelect}
                  onProductDeselect={handleProductDeselect}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Panel modal de detalles centrado */}
      <ProductModalWidget
        product={selectedProduct}
        open={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        showApproveReject={selectedProduct && String(selectedProduct.status).toLowerCase() === 'pending'}
        onApprove={() => handleApprove(selectedProduct)}
        onReject={() => handleReject(selectedProduct)}
        convertBase64ToDataUrl={convertBase64ToDataUrl}
        onShowAdditionalImages={(additionalImages) => {
          setSliderImages(additionalImages);
          setSliderOpen(true);
        }}
        onMarkAsReviewed={handleMarkAsReviewed}
        showReviewButton={selectedProduct}
      />
      <AdditionalImagesSliderModal images={sliderImages} open={sliderOpen} onClose={() => setSliderOpen(false)} />

      {/* Pop-up de aprobación/rechazo */}
      {showPopup && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: '500px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            {popupType === 'reject' ? (
              <>
                <h3 style={{ marginTop: 0 }}>Rechazar producto</h3>
                <p>Por favor, indica el motivo del rechazo:</p>
                <textarea
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  rows={4}
                  style={{ width: '100%', borderRadius: 6, border: '1px solid #ccc', padding: 8, marginBottom: 16 }}
                  placeholder="Motivo del rechazo..."
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button onClick={handleClosePopup} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#eee', cursor: 'pointer' }}>Cancelar</button>
                  <button onClick={handleConfirm} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#e74c3c', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }} disabled={!rejectReason.trim()}>Rechazar</button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>¿Estas seguro de aprobar este producto?</h3>
                <p style={{padding: '1rem 0 2rem'}}>Al aprobar el producto, se publicara en segundo plano</p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button onClick={handleClosePopup} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#eee', cursor: 'pointer' }}>Cancelar</button>
                  <button onClick={handleConfirm} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#27ae60', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Aprobar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductReviewClient; 